/**
 * The decisions multi-select makes, as functions rather than as expressions inside a 2,000-line
 * component.
 *
 * Three regressions in one session came from this logic while it had no coverage: a duplicate
 * anchored on the wrong task and landed inside its own pathway, a bare modifier keypress wiped a
 * selection before its second key arrived, and a delete quietly took pathways nobody had picked.
 * Each was a one-line rule with no test, and each was found by driving a browser.
 */
import type { Task } from '@/types/experience';

/**
 * Selected tasks that host a pathway which is NOT wholly selected.
 *
 * Deleting one would leave its pathway anchored to nothing — the same thing the single-task delete
 * refuses. Bulk delete used to expand to the subtree instead: more powerful, but it made the host
 * rule look optional and removed pathways nobody had chosen. Dismantling first is cheap, because a
 * pathway has its own delete.
 */
export function orphanedHosts(tasks: Pick<Task, 'id' | 'pathwayOf'>[], selectedIds: Set<string>): Set<string> {
  const hosts = new Set<string>();
  for (const task of tasks) {
    if (task.pathwayOf && selectedIds.has(task.pathwayOf) && !selectedIds.has(task.id)) {
      hosts.add(task.pathwayOf);
    }
  }
  return hosts;
}

/**
 * The task a duplicate is placed after.
 *
 * The last selected ROOT, not the last selected task. Roots are what land in the anchor's lane, so
 * the anchor has to be one: for a host together with the pathway hanging off it, the last task in
 * board order is a pathway MEMBER, and anchoring there put the copied host inside the original's
 * pathway instead of on the spine beside it.
 *
 * `members` must be in board order, and `parents` is `resolveSelectionParents`' answer. At least one
 * member always resolves to a root — the topmost cannot have a selected ancestor — so the fallback
 * only covers an empty selection.
 */
export function duplicateAnchorId(
  members: Pick<Task, 'id'>[],
  parents: Map<string, string | null>,
): string | undefined {
  const roots = members.filter((m) => !parents.get(m.id));
  return (roots[roots.length - 1] ?? members[members.length - 1])?.id;
}

/** What a keypress means to the board. `other` is a modifier combination we do not act on. */
export type SelectionShortcut = 'escape' | 'copy' | 'paste' | 'undo' | 'redo' | 'other';

/**
 * Read a keypress, so the board can act and so select mode knows whether to close.
 *
 * The rule that matters is `key.length === 1`. Holding Cmd fires a keydown whose `metaKey` is
 * ALREADY true, so treating any modifier-bearing event as a shortcut closed select mode the instant
 * a shortcut was reached for — destroying a selection that may have taken many clicks, with no
 * warning and nothing to undo it with. A modifier and a LETTER is deliberate; a modifier alone is
 * someone starting to type.
 *
 * Both modifiers, so Cmd on a Mac and Ctrl elsewhere.
 */
export function selectionShortcut(
  e: Pick<KeyboardEvent, 'key' | 'ctrlKey' | 'metaKey' | 'shiftKey'>,
): SelectionShortcut | null {
  if (e.key === 'Escape') return 'escape';
  if (!e.ctrlKey && !e.metaKey) return null;
  // 'Meta', 'Shift', 'Control', 'ArrowDown' — anything that is not a single character.
  if (e.key.length !== 1) return null;
  if (e.key === 'c') return 'copy';
  if (e.key === 'v') return 'paste';
  if (e.key === 'y' || e.key === 'Z' || (e.key === 'z' && e.shiftKey)) return 'redo';
  if (e.key === 'z') return 'undo';
  return 'other';
}
